import React from "react";
import { motion } from "framer-motion";
import {
  Wrench,
  Snowflake,
  CircleDot,
  Gauge,
  ClipboardCheck,
  Disc3,
  ArrowUpRight,
} from "lucide-react";

const items = [
  {
    title: "Service & Reparation",
    desc: "Löpande service, slitdelar och mekaniska reparationer med fokus på att bilen ska fungera som den ska.",
    icon: Wrench,
    featured: true,
    badge: "Populärast",
    image:
      "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxjYXIlMjBtZWNoYW5pYyUyMGRpYWdub3N0aWMlMjB0b29scyUyMHdvcmtzaG9wJTIwcmVwYWlyJTIwZW5naW5lfGVufDB8fHx8MTc5MTE5MjQwM3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Däckbyte & Däckhotell",
    desc: "Säsongsdäckbyte, balansering och smidig hantering av däcken mellan bytena.",
    icon: CircleDot,
  },
  {
    title: "AC-Service",
    desc: "Kontroll, läcksökning och service av AC-systemet för ett behagligt kupéklimat.",
    icon: Snowflake,
  },
  {
    title: "Felsökning & Diagnostik",
    desc: "Systematisk felsökning när varningslampor tänds eller bilen inte beter sig som den ska.",
    icon: Gauge,
  },
  {
    title: "Besiktningshjälp",
    desc: "Genomgång inför besiktning och hjälp med sådant som behöver åtgärdas före eller efter kontrollen.",
    icon: ClipboardCheck,
  },
  {
    title: "Bromsar & Avgassystem",
    desc: "Kontroll och åtgärd av bromsar och avgassystem när delar är slitna eller behöver bytas.",
    icon: Disc3,
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.8, 0.2, 1],
      delay: i * 0.07,
    },
  }),
};

export default function Services() {
  return (
    <section id="tjanster" className="relative py-24 lg:py-36 bg-white">
      <div className="max-w-[1360px] mx-auto px-5 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-6 lg:items-end mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">01 — Tjänster</span>
            <h2 className="hero-text mt-5 text-[9vw] sm:text-[6vw] lg:text-[4.4vw] xl:text-[72px]">
              Rätt hjälp för bilen,<br />
              <span className="italic text-[#1F3C88]">utan onödigt krångel.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 text-slate-600 text-[1.02rem] leading-relaxed max-w-md"
          >
            Från löpande service och däck till felsökning och reparation. ZAM samlar
            det viktigaste på ett ställe och gör vägen från problem till åtgärd
            så tydlig som möjligt.
          </motion.p>
        </div>

        {/* Symmetric 2 × 3 service grid */}
        <div
          data-testid="services-bento-grid"
          className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
        >
          {items.map((item, i) => {
            const Icon = item.icon;
            const isFeatured = item.featured === true;
            return (
              <motion.a
                key={item.title}
                href="#boka"
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`service-card-${i}`}
                className="service-card group relative rounded-[22px] border border-slate-200 bg-white overflow-hidden min-h-[280px] lg:min-h-[310px]"
              >
                {isFeatured && item.image && (
                  <>
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.04] transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/40 to-slate-900/10" />
                  </>
                )}

                <div
                  className={`relative h-full flex flex-col ${
                    isFeatured ? "p-7 lg:p-8 text-white" : "p-7 lg:p-8"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`h-11 w-11 rounded-full grid place-items-center ${
                        isFeatured
                          ? "bg-white/15 border border-white/25 text-white"
                          : "bg-[#EBF3FF] text-[#1F3C88]"
                      }`}
                    >
                      <Icon strokeWidth={1.7} className="h-5 w-5" />
                    </div>
                    {item.badge && (
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase font-bold bg-[#D52027] text-white px-2.5 py-1 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {!item.badge && (
                      <ArrowUpRight
                        className={`arrow h-5 w-5 ${
                          isFeatured ? "text-white/80" : "text-slate-400"
                        }`}
                      />
                    )}
                  </div>

                  <div className="mt-auto pt-10">
                    <h3
                      className={`font-display tracking-tight ${
                        isFeatured
                          ? "text-[1.8rem] lg:text-[2rem] leading-[1.05]"
                          : "text-[1.8rem] lg:text-[2rem] leading-[1.05] text-slate-900"
                      }`}
                    >
                      {item.title}
                    </h3>
                    <p
                      className={`mt-3 text-[0.95rem] leading-relaxed max-w-sm ${
                        isFeatured ? "text-white/80" : "text-slate-600"
                      }`}
                    >
                      {item.desc}
                    </p>
                    {isFeatured && (
                      <div className="mt-6 inline-flex items-center gap-2 text-white/90 text-sm font-semibold">
                        Boka service
                        <ArrowUpRight className="arrow h-4 w-4" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
